import Phaser from 'phaser';
import { Key9Scene } from './game/Key9Scene';
import type { Key9Puzzle, Key9Result } from './types/puzzle';
import { validatePuzzleShape, resultLabel } from './logic/key9Validator';
import {
  clearActiveFullProgress,
  getSavedDayProgress,
  getSavedKey9Result,
  loadSave,
  saveFullDone,
  saveFullProgress,
  saveKey9Missed,
  saveKey9Unlock,
  type DayProgress
} from './storage/localSave';
import {
  buildFullShareText,
  buildPracticeShareText,
  buildPreviousResultDrawerModel,
  buildShareCardModel,
  buildShareText,
  formatTime,
  type PreviousResultDrawerModel,
  type ShareCardModel,
  type ShareCardVariant
} from './logic/share';
import { getNextPuzzleCountdownModel } from './logic/dailyCountdown';
import { createDailyPuzzleIdentity, type DailyPuzzleIdentity } from './logic/puzzleIdentity';
import {
  createBootSessionToken,
  createHydratedBootState,
  shouldApplyBootToken
} from './logic/bootHydration';
import {
  getDayFlowVisibility,
  getDayUiStateForFlow,
  getDayUiStateForPhase,
  getInlineKey9Actions,
  getMissedPanelCopy,
  getResultActionSheet,
  getTerminalResultLayout,
  shouldApplyStatusPhase,
  type FullSubpanelState,
  type InlineKey9ActionState,
  type ResultActionSheetState,
  type DayUiState
} from './logic/dayFlow';
import { resolveDailyPuzzle, type PuzzleManifest } from './logic/dailyPuzzle';
import { getGameControlState, type GameControlPhase } from './logic/gameControls';

type PuzzleLoadContext = {
  puzzle: Key9Puzzle;
  identity: DailyPuzzleIdentity;
};

let game: Phaser.Game | null = null;
let currentPuzzle: Key9Puzzle | null = null;
let currentIdentity: DailyPuzzleIdentity | null = null;
let timerId: number | null = null;
let startMs = Date.now();
let latestKey9Result: Key9Result | null = null;
let latestProgress: DayProgress | null = null;
let latestPracticeResult: Key9Result | null = null;
let latestShareVariant: ShareCardVariant | null = null;
let previousResultShareVariant: ShareCardVariant | null = null;
let lastMisses = 0;
let buttonsBound = false;
let eventsBound = false;
let nextPuzzleTimerId: number | null = null;
let activeBootToken = 0;

const containerId = 'daysudoku-game';

async function boot() {
  const bootSession = createBootSessionToken(activeBootToken);
  activeBootToken = bootSession.activeBootToken;
  const token = bootSession.token;
  const container = document.getElementById(containerId);
  if (!container) return;
  bindButtons();
  bindEvents();

  const loaded = await loadTodayPuzzle();
  if (!shouldApplyBootToken(activeBootToken, token)) {
    console.debug('DaySudoku boot stale async result ignored.');
    return;
  }
  currentPuzzle = loaded.puzzle;
  currentIdentity = loaded.identity;
  const errors = validatePuzzleShape(currentPuzzle);
  if (errors.length) {
    console.error('Invalid puzzle:', errors);
    container.textContent = 'Puzzle data error.';
    return;
  }

  const save = loadSave();
  const bootState = createHydratedBootState({
    puzzle: currentPuzzle,
    identity: currentIdentity,
    save,
    fallbackDate: currentPuzzle.date
  });
  warnIfStaleDailyProgressIgnored(bootState.ignoredStaleSave);
  if (!shouldApplyBootToken(activeBootToken, token)) {
    console.debug('DaySudoku boot stale hydrated state ignored.');
    return;
  }
  latestProgress = bootState.progress;
  latestKey9Result = bootState.key9Result;

  const flow = bootState.flow;
  startMs = Date.now();
  game?.destroy(true);
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: containerId,
    width: 430,
    height: 630,
    backgroundColor: '#f5f7fb',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH
    },
    scene: []
  });
  game.scene.add('Key9Scene', Key9Scene, true, bootState.sceneStart);
  setStatusLabel(flow.statusLabel);
  const visibility = getDayFlowVisibility(flow);
  applyGameShellState(getDayUiStateForFlow(flow));
  updateGameControls(visibility.showFullToolbar ? 'full_in_progress' : flow.practice ? 'key9_practice' : 'key9_in_progress');

  if (flow.panel === 'full_done' && bootState.progress) {
    showFullDone(bootState.progress, save.currentStreak);
    stopTimer();
    setMisses(bootState.progress.key9Result?.misses ?? 0);
    const timerEl = document.getElementById('ds-timer');
    if (timerEl) timerEl.textContent = formatTime(bootState.progress.fullSeconds ?? 0);
    return;
  }

  if (flow.panel === 'key9_unlocked' && latestKey9Result) {
    showKey9Unlocked(latestKey9Result);
    stopTimer();
    setMisses(latestKey9Result.misses);
    const timerEl = document.getElementById('ds-timer');
    if (timerEl) timerEl.textContent = formatTime(latestKey9Result.seconds);
    return;
  }

  if (flow.panel === 'missed' && latestKey9Result) {
    showKey9Missed(latestKey9Result);
    stopTimer();
    setMisses(latestKey9Result.misses);
    return;
  }

  startTimer();
}

async function loadTodayPuzzle(): Promise<PuzzleLoadContext> {
  const today = localDateKey();
  const year = today.slice(0, 4);
  const fallbackUrls = [`/puzzles/${year}/${today}.json`, '/puzzles/2026/2026-06-12.json'];

  try {
    const manifestRes = await fetch('/puzzles/manifest.json', { cache: 'no-cache' });
    if (manifestRes.ok) {
      const manifest = await manifestRes.json() as PuzzleManifest;
      const resolution = resolveDailyPuzzle(manifest, today);
      try {
        const puzzle = await fetchPuzzleJson(resolution.entry.path);
        return {
          puzzle,
          identity: createDailyPuzzleIdentity({
            dailyDate: today,
            manifestEntry: resolution.entry,
            puzzle
          })
        };
      } catch (error) {
        console.warn(`Daily puzzle fetch failed for ${resolution.entry.path}; using fallback puzzle.`, error);
      }
    } else {
      console.warn(`Puzzle manifest fetch failed with status ${manifestRes.status}; using fallback puzzle.`);
    }
  } catch (error) {
    console.warn('Puzzle manifest unavailable; using fallback puzzle.', error);
  }

  for (const url of fallbackUrls) {
    try {
      const puzzle = await fetchPuzzleJson(url);
      return {
        puzzle,
        identity: createDailyPuzzleIdentity({
          dailyDate: today,
          puzzle,
          puzzlePath: url
        })
      };
    } catch {
      // try next fallback
    }
  }
  throw new Error('No puzzle found');
}

async function fetchPuzzleJson(url: string): Promise<Key9Puzzle> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) {
    throw new Error(`Puzzle fetch failed with status ${res.status}`);
  }
  return await res.json() as Key9Puzzle;
}

function localDateKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function currentDailyDate(): string {
  return currentIdentity?.dailyDate ?? currentPuzzle?.date ?? localDateKey();
}

function warnIfStaleDailyProgressIgnored(ignored: boolean) {
  if (!ignored) return;
  console.warn('Ignored saved DaySudoku progress for a different puzzle/day.');
}

function bindButtons() {
  if (buttonsBound) return;
  buttonsBound = true;
  document.getElementById('ds-undo')?.addEventListener('click', () => getScene()?.undo());
  document.getElementById('ds-erase')?.addEventListener('click', () => getScene()?.erase());
  document.getElementById('ds-notes')?.addEventListener('click', () => {
    const enabled = getScene()?.toggleNotes() ?? false;
    setNotesButtonState(enabled);
  });
  document.getElementById('ds-hint')?.addEventListener('click', () => getScene()?.hint());
  document.getElementById('ds-view-result')?.addEventListener('click', showPreviousResultDrawer);
  document.getElementById('ds-previous-result-close')?.addEventListener('click', hidePreviousResultDrawer);
  document.getElementById('ds-previous-close-secondary')?.addEventListener('click', hidePreviousResultDrawer);
  document.getElementById('ds-previous-share')?.addEventListener('click', sharePreviousResult);
  document.getElementById('ds-previous-load-new-puzzle')?.addEventListener('click', loadNewPuzzle);
  document.getElementById('ds-restart-key9')?.addEventListener('click', showRestartKey9Confirm);
  document.getElementById('ds-restart-key9-confirm-cancel')?.addEventListener('click', hideRestartKey9Confirm);
  document.getElementById('ds-restart-key9-confirm-submit')?.addEventListener('click', restartKey9PracticeFromFull);
  document.getElementById('ds-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-sheet-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-inline-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-stage-load-new-puzzle')?.addEventListener('click', loadNewPuzzle);
  document.getElementById('ds-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-sheet-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-inline-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-continue')?.addEventListener('click', continueSudoku);
  document.getElementById('ds-sheet-continue')?.addEventListener('click', continueSudoku);
  document.getElementById('ds-inline-continue')?.addEventListener('click', continueSudoku);
}

function bindEvents() {
  if (eventsBound) return;
  eventsBound = true;
  window.addEventListener('daysudoku:key9-status', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { misses: number; phase?: GameControlPhase; notesMode?: boolean; canHint?: boolean };
    const increased = detail.misses > lastMisses;
    setMisses(detail.misses);
    if (increased) {
      const missEl = document.getElementById('ds-misses');
      missEl?.classList.remove('miss-pop');
      window.setTimeout(() => missEl?.classList.add('miss-pop'), 0);
    }
    lastMisses = detail.misses;
    const appliedStatusPhase = detail.phase ? shouldApplyStatusPhase(getCurrentGameShellState(), detail.phase) : false;
    if (detail.phase && appliedStatusPhase) {
      updateGameControls(detail.phase, detail.notesMode ?? false, detail.canHint ?? false);
    }
    if (appliedStatusPhase && detail.phase === 'full_in_progress') setStatusLabel('Continue today’s Sudoku');
    if (appliedStatusPhase && detail.phase === 'key9_practice') setStatusLabel('Key9 Practice');
  }) as EventListener);

  window.addEventListener('daysudoku:key9-unlocked', ((event: Event) => onKey9Unlocked((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-missed', ((event: Event) => onKey9Missed((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-practice-unlocked', ((event: Event) => onKey9PracticeUnlocked((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-practice-missed', (() => {
    if (latestKey9Result) showKey9Missed(latestKey9Result);
    stopTimer();
  }) as EventListener);
  window.addEventListener('daysudoku:full-progress', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { board: string[]; notes?: string[]; fullHints?: number };
    if (!currentPuzzle) return;
    const saved = saveFullProgress(currentDailyDate(), detail.board, detail.notes, detail.fullHints, currentIdentity ?? undefined);
    latestProgress = getSavedDayProgress(saved, currentDailyDate(), currentIdentity);
  }) as EventListener);
  window.addEventListener('daysudoku:full-complete', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { board: string[]; notes?: string[]; fullHints?: number; seconds: number };
    onFullComplete(detail.board, detail.seconds, detail.notes, detail.fullHints);
  }) as EventListener);
}

function getScene(): Key9Scene | null {
  if (!game) return null;
  return game.scene.getScene('Key9Scene') as Key9Scene;
}

function startTimer() {
  if (timerId) window.clearInterval(timerId);
  timerId = window.setInterval(() => {
    const seconds = Math.round((Date.now() - startMs) / 1000);
    const el = document.getElementById('ds-timer');
    if (el) el.textContent = formatTime(seconds);
  }, 1000);
}

function stopTimer() {
  if (timerId) window.clearInterval(timerId);
  timerId = null;
}

function setMisses(misses: number) {
  const missEl = document.getElementById('ds-misses');
  if (missEl) missEl.textContent = `Misses ${misses}/9`;
}

function startKey9Practice() {
  applyGameShellState(getDayUiStateForPhase('key9_practice'));
  hideFullAuxiliaryPanels();
  hideResultPanel();
  latestPracticeResult = null;
  latestShareVariant = null;
  getScene()?.retryKey9Practice();
  startMs = Date.now();
  startTimer();
  setStatusLabel('Key9 Practice');
  setMisses(0);
  updateGameControls('key9_practice');
}

function continueSudoku() {
  applyGameShellState(getDayUiStateForPhase('full_in_progress'));
  hideFullAuxiliaryPanels();
  hideResultPanel();
  latestPracticeResult = null;
  latestShareVariant = null;
  getScene()?.continueSudoku();
  startMs = Date.now();
  startTimer();
  setStatusLabel('Continue today’s Sudoku');
  updateGameControls('full_in_progress');
}

function onKey9Unlocked(result: Key9Result) {
  stopTimer();
  latestKey9Result = result;
  latestPracticeResult = null;
  const saved = currentPuzzle ? saveKey9Unlock(currentDailyDate(), result, currentIdentity ?? undefined) : loadSave();
  latestProgress = currentPuzzle ? getSavedDayProgress(saved, currentDailyDate(), currentIdentity) : null;
  showKey9Unlocked(result);
}

function onKey9Missed(result: Key9Result) {
  stopTimer();
  latestKey9Result = result;
  latestPracticeResult = null;
  const saved = currentPuzzle ? saveKey9Missed(currentDailyDate(), result, currentIdentity ?? undefined) : loadSave();
  latestProgress = currentPuzzle ? getSavedDayProgress(saved, currentDailyDate(), currentIdentity) : null;
  showKey9Missed(result);
}

function onKey9PracticeUnlocked(result: Key9Result) {
  stopTimer();
  latestPracticeResult = result;
  showPanel({
    title: 'Key9 Practice Complete',
    summary: `Practice run complete · Misses ${result.misses}/9 · ${formatTime(result.seconds)}`,
    status: 'Key9 Practice',
    doneCopy: '',
    inlineState: 'key9_practice_complete',
    sheetState: 'key9_missed',
    showPractice: true,
    practiceText: 'Try Again',
    showContinue: true,
    continueText: 'Continue Sudoku',
    shareText: 'Share Result',
    shareVariant: 'key9_practice_complete',
    key9Result: latestKey9Result,
    practiceResult: result,
    progress: latestProgress
  });
}

function onFullComplete(board: string[], seconds: number, notes?: string[], fullHints?: number) {
  stopTimer();
  if (!currentPuzzle) return;
  const saved = saveFullDone(currentDailyDate(), board, seconds, notes, fullHints, currentIdentity ?? undefined);
  latestProgress = getSavedDayProgress(saved, currentDailyDate(), currentIdentity);
  if (!latestProgress) return;
  showFullDone(latestProgress, saved.currentStreak);
}

function showKey9Unlocked(result: Key9Result) {
  showPanel({
    title: 'Key9 Unlocked',
    summary: 'You placed 1–9. Now finish the Sudoku.',
    status: 'Key9 Unlocked',
    doneCopy: '',
    inlineState: 'key9_unlocked',
    sheetState: 'key9_unlocked',
    showPractice: false,
    practiceText: 'Try Again',
    showContinue: true,
    continueText: 'Continue Sudoku',
    shareText: 'Share My Key9',
    shareVariant: 'key9_unlocked',
    key9Result: result,
    progress: latestProgress
  });
}

function showKey9Missed(result: Key9Result) {
  const copy = getMissedPanelCopy();
  showPanel({
    title: copy.title,
    summary: copy.body,
    status: 'Key9 Missed',
    doneCopy: '',
    inlineState: 'key9_missed',
    sheetState: 'key9_missed',
    showPractice: true,
    practiceText: copy.primaryButton,
    showContinue: true,
    continueText: copy.secondaryButton,
    shareText: copy.tertiaryButton,
    shareVariant: 'key9_missed',
    key9Result: result,
    progress: latestProgress
  });
}

function showFullDone(progress: DayProgress, streak?: number) {
  const key9 = progress.key9Result;
  const fullHints = progress.fullHints ?? 0;
  const fullCopy = fullHints > 0
    ? `Full Sudoku: Completed · Hints ${fullHints}`
    : 'Full Sudoku: Clean completion · Hints 0';
  const timeCopy = progress.fullSeconds ? ` · ${formatTime(progress.fullSeconds)}` : '';
  const streakCopy = streak && streak > 1 ? `\n${streak}-day streak` : '';
  const key9Copy = key9
    ? `Key9 result: ${resultLabel(key9.status)} · Misses ${key9.misses}/9`
    : 'Key9 result: Unlocked';
  showPanel({
    title: 'Today’s DaySudoku is done.',
    summary: `${key9Copy}\n${fullCopy}${timeCopy}${streakCopy}`,
    status: 'Done for today',
    doneCopy: 'You’re done for today.',
    inlineState: 'full_done',
    sheetState: 'full_done',
    showPractice: true,
    practiceText: 'Practice Again',
    showContinue: false,
    continueText: 'Continue Sudoku',
    shareText: 'Share Today’s Win',
    shareVariant: 'full_done',
    key9Result: key9,
    progress
  });
}

function showPanel(config: {
  title: string;
  summary: string;
  status: string;
  doneCopy: string;
  inlineState: InlineKey9ActionState;
  sheetState: ResultActionSheetState;
  showPractice: boolean;
  practiceText: string;
  showContinue: boolean;
  continueText: string;
  shareText: string;
  shareVariant: ShareCardVariant;
  key9Result?: Key9Result | null;
  practiceResult?: Key9Result | null;
  progress?: DayProgress | null;
}) {
  hideFullAuxiliaryPanels();
  applyGameShellState(getDayUiStateForPhase(config.inlineState));
  const panel = document.getElementById('ds-result');
  const card = document.querySelector('[data-game-card]');
  const title = document.getElementById('ds-result-title');
  const summary = document.getElementById('ds-result-summary');
  const doneCopy = document.getElementById('ds-done-copy');
  const practiceButton = document.getElementById('ds-practice') as HTMLButtonElement | null;
  const continueButton = document.getElementById('ds-continue') as HTMLButtonElement | null;
  const shareButton = document.getElementById('ds-share') as HTMLButtonElement | null;
  const inlineStrip = document.getElementById('ds-key9-actions');
  const inlineStatus = document.getElementById('ds-key9-actions-status');
  const inlinePracticeButton = document.getElementById('ds-inline-practice') as HTMLButtonElement | null;
  const inlineContinueButton = document.getElementById('ds-inline-continue') as HTMLButtonElement | null;
  const inlineShareButton = document.getElementById('ds-inline-share') as HTMLButtonElement | null;
  const sheet = document.getElementById('ds-action-sheet');
  const sheetTitle = document.getElementById('ds-sheet-title');
  const sheetSummary = document.getElementById('ds-sheet-summary');
  const sheetDoneCopy = document.getElementById('ds-sheet-done-copy');
  const sheetPracticeButton = document.getElementById('ds-sheet-practice') as HTMLButtonElement | null;
  const sheetContinueButton = document.getElementById('ds-sheet-continue') as HTMLButtonElement | null;
  const sheetShareButton = document.getElementById('ds-sheet-share') as HTMLButtonElement | null;
  if (title) title.textContent = config.title;
  if (summary) summary.textContent = config.summary;
  if (doneCopy) {
    doneCopy.textContent = config.doneCopy;
    doneCopy.hidden = config.doneCopy.length === 0;
  }
  if (practiceButton) {
    practiceButton.textContent = config.practiceText;
    practiceButton.hidden = !config.showPractice;
  }
  if (continueButton) {
    continueButton.textContent = config.continueText;
    continueButton.hidden = !config.showContinue;
  }
  if (shareButton) shareButton.textContent = config.shareText;
  latestShareVariant = config.shareVariant;
  renderStageShareCard(buildShareCardModel({
    date: currentDailyDate(),
    variant: config.shareVariant,
    key9Result: config.key9Result ?? latestKey9Result,
    practiceResult: config.practiceResult ?? latestPracticeResult,
    progress: config.progress ?? latestProgress
  }));

  const inlineConfig = getInlineKey9Actions(config.inlineState);
  const layoutConfig = getTerminalResultLayout(config.inlineState);
  const inlineActionCount = Object.keys(inlineConfig.buttons).length;
  if (inlineStrip) {
    inlineStrip.hidden = !inlineConfig.visible;
    inlineStrip.dataset.state = inlineConfig.variant;
    inlineStrip.dataset.actionCount = String(inlineActionCount);
    inlineStrip.dataset.placement = layoutConfig.placement;
    inlineStrip.dataset.overlaysBoard = String(layoutConfig.overlaysBoard);
  }
  if (inlineStatus) inlineStatus.textContent = inlineConfig.status;
  if (inlinePracticeButton) {
    inlinePracticeButton.textContent = inlineConfig.buttons.practice ?? config.practiceText;
    inlinePracticeButton.hidden = !inlineConfig.buttons.practice;
    inlinePracticeButton.className = getInlineActionClass(inlineConfig.primaryAction, 'practice');
  }
  if (inlineContinueButton) {
    inlineContinueButton.textContent = inlineConfig.buttons.continue ?? config.continueText;
    inlineContinueButton.hidden = !inlineConfig.buttons.continue;
    inlineContinueButton.className = getInlineActionClass(inlineConfig.primaryAction, 'continue');
  }
  if (inlineShareButton) {
    inlineShareButton.textContent = inlineConfig.buttons.share ?? config.shareText;
    inlineShareButton.hidden = !inlineConfig.buttons.share;
    inlineShareButton.className = getInlineActionClass(inlineConfig.primaryAction, 'share');
  }
  panel?.toggleAttribute('data-inline-actions', inlineConfig.visible);

  if (sheetTitle) sheetTitle.textContent = config.title;
  if (sheetSummary) sheetSummary.textContent = config.summary;
  if (sheetDoneCopy) {
    sheetDoneCopy.textContent = config.sheetState === 'full_done' ? config.doneCopy : '';
    sheetDoneCopy.hidden = sheetDoneCopy.textContent.length === 0;
  }

  const sheetConfig = getResultActionSheet(config.sheetState);
  const actionCount = Object.keys(sheetConfig.buttons).length;
  if (sheet) {
    sheet.hidden = !sheetConfig.visible;
    sheet.dataset.state = sheetConfig.variant;
    sheet.dataset.actionCount = String(actionCount);
  }
  card?.classList.toggle('sheet-open', sheetConfig.visible);
  if (sheetPracticeButton) {
    sheetPracticeButton.textContent = sheetConfig.buttons.practice ?? config.practiceText;
    sheetPracticeButton.hidden = !sheetConfig.buttons.practice;
  }
  if (sheetContinueButton) {
    sheetContinueButton.textContent = sheetConfig.buttons.continue ?? config.continueText;
    sheetContinueButton.hidden = !sheetConfig.buttons.continue;
  }
  if (sheetShareButton) {
    sheetShareButton.textContent = sheetConfig.buttons.share ?? config.shareText;
    sheetShareButton.hidden = !sheetConfig.buttons.share;
  }
  setStatusLabel(config.status);
  updateGameControls(config.inlineState === 'full_done' ? 'full_done' : config.inlineState);
  if (panel) panel.hidden = false;
  syncNextPuzzleCountdownTimer();
}

function hideResultPanel() {
  const panel = document.getElementById('ds-result');
  const inlineStrip = document.getElementById('ds-key9-actions');
  const sheet = document.getElementById('ds-action-sheet');
  const card = document.querySelector('[data-game-card]');
  if (panel) panel.hidden = true;
  panel?.removeAttribute('data-inline-actions');
  if (inlineStrip) inlineStrip.hidden = true;
  if (sheet) sheet.hidden = true;
  card?.classList.remove('sheet-open');
  syncNextPuzzleCountdownTimer();
}

function showPreviousResultDrawer() {
  if (!currentPuzzle || !canShowFullSecondaryActions()) return;
  hideRestartKey9Confirm();
  const official = getOfficialResultContext();
  const model = buildPreviousResultDrawerModel({
    date: currentDailyDate(),
    key9Result: official.key9Result,
    progress: official.progress
  });
  if (!model.visible) return;
  previousResultShareVariant = model.variant;
  renderPreviousResultDrawer(model);
  const drawer = document.getElementById('ds-previous-result') as HTMLElement | null;
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  if (drawer) drawer.hidden = false;
  if (card) card.dataset.previousResultVisible = 'true';
  setFullSubpanelState('view-result');
  syncNextPuzzleCountdownTimer();
}

function hidePreviousResultDrawer() {
  const drawer = document.getElementById('ds-previous-result') as HTMLElement | null;
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  if (drawer) drawer.hidden = true;
  if (card) card.dataset.previousResultVisible = 'false';
  if (getFullSubpanelState() === 'view-result') setFullSubpanelState('none');
  previousResultShareVariant = null;
  syncNextPuzzleCountdownTimer();
}

function showRestartKey9Confirm() {
  if (!canShowFullSecondaryActions()) return;
  hidePreviousResultDrawer();
  const confirm = document.getElementById('ds-restart-key9-confirm') as HTMLElement | null;
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  if (confirm) confirm.hidden = false;
  if (card) card.dataset.restartKey9ConfirmVisible = 'true';
  setFullSubpanelState('restart-key9');
}

function hideRestartKey9Confirm() {
  const confirm = document.getElementById('ds-restart-key9-confirm') as HTMLElement | null;
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  if (confirm) confirm.hidden = true;
  if (card) card.dataset.restartKey9ConfirmVisible = 'false';
  if (getFullSubpanelState() === 'restart-key9') setFullSubpanelState('none');
}

function hideFullAuxiliaryPanels() {
  hidePreviousResultDrawer();
  hideRestartKey9Confirm();
}

function restartKey9PracticeFromFull() {
  if (!currentPuzzle || !canShowFullSecondaryActions()) return;
  const saved = clearActiveFullProgress(currentDailyDate(), currentIdentity ?? undefined);
  latestProgress = getSavedDayProgress(saved, currentDailyDate(), currentIdentity) ?? latestProgress;
  latestKey9Result = latestProgress?.key9Result ?? getSavedKey9Result(saved, currentDailyDate(), currentIdentity) ?? latestKey9Result;
  hideFullAuxiliaryPanels();
  startKey9Practice();
}

function canShowFullSecondaryActions(): boolean {
  const shellState = getCurrentGameShellState();
  return shellState.showFullToolbar && !shellState.showTerminalResult;
}

function getOfficialResultContext(): { progress: DayProgress | null; key9Result: Key9Result | null } {
  if (!currentPuzzle) return { progress: latestProgress, key9Result: latestKey9Result };
  const save = loadSave();
  const progress = getSavedDayProgress(save, currentDailyDate(), currentIdentity) ?? latestProgress;
  const key9Result = progress?.key9Result ?? getSavedKey9Result(save, currentDailyDate(), currentIdentity) ?? latestKey9Result;
  latestProgress = progress ?? latestProgress;
  latestKey9Result = key9Result ?? latestKey9Result;
  return {
    progress: progress ?? null,
    key9Result: key9Result ?? null
  };
}

function renderStageShareCard(model: ShareCardModel) {
  renderShareCardModel(model, {
    brand: 'ds-stage-brand',
    tagline: 'ds-stage-tagline',
    title: 'ds-stage-result-title',
    body: 'ds-stage-result-body',
    metrics: 'ds-stage-metrics',
    key9Label: 'ds-stage-key9-label',
    key9Cells: 'ds-stage-key9-cells',
    fullBadge: 'ds-stage-full-badge',
    footer: 'ds-stage-footer',
    nextPuzzle: 'ds-stage-next-puzzle',
    nextPuzzleLoad: 'ds-stage-load-new-puzzle',
    countdownFullDone: model.title === 'Today’s DaySudoku is done.'
  });
}

function renderPreviousResultDrawer(model: PreviousResultDrawerModel) {
  const title = document.getElementById('ds-previous-result-title');
  const share = document.getElementById('ds-previous-share') as HTMLButtonElement | null;
  const close = document.getElementById('ds-previous-close-secondary') as HTMLButtonElement | null;
  if (title) title.textContent = model.title;
  if (share) share.textContent = model.shareLabel;
  if (close) close.textContent = model.closeLabel;
  renderShareCardModel(model.card, {
    brand: 'ds-previous-brand',
    tagline: 'ds-previous-tagline',
    title: 'ds-previous-card-title',
    body: 'ds-previous-card-body',
    metrics: 'ds-previous-metrics',
    key9Label: 'ds-previous-key9-label',
    key9Cells: 'ds-previous-key9-cells',
    fullBadge: 'ds-previous-full-badge',
    footer: 'ds-previous-footer',
    nextPuzzle: 'ds-previous-next-puzzle',
    nextPuzzleLoad: 'ds-previous-load-new-puzzle',
    countdownFullDone: model.variant === 'full_done'
  });
}

function renderShareCardModel(model: ShareCardModel, ids: {
  brand: string;
  tagline: string;
  title: string;
  body: string;
  metrics: string;
  key9Label: string;
  key9Cells: string;
  fullBadge: string;
  footer: string;
  nextPuzzle: string;
  nextPuzzleLoad: string;
  countdownFullDone?: boolean;
}) {
  const brand = document.getElementById(ids.brand);
  const tagline = document.getElementById(ids.tagline);
  const title = document.getElementById(ids.title);
  const body = document.getElementById(ids.body);
  const metrics = document.getElementById(ids.metrics);
  const key9Label = document.getElementById(ids.key9Label);
  const key9Cells = document.getElementById(ids.key9Cells);
  const fullBadge = document.getElementById(ids.fullBadge);
  const footer = document.getElementById(ids.footer);
  const nextPuzzle = document.getElementById(ids.nextPuzzle);
  const nextPuzzleLoad = document.getElementById(ids.nextPuzzleLoad) as HTMLButtonElement | null;

  if (brand) brand.textContent = model.brand;
  if (tagline) tagline.textContent = model.tagline;
  if (title) title.textContent = model.title;
  if (body) {
    body.textContent = model.body;
    body.hidden = model.body.length === 0;
  }
  if (key9Label) key9Label.textContent = model.key9Strip.label;
  if (footer) footer.textContent = model.footer;
  if (nextPuzzle) {
    nextPuzzle.textContent = model.nextPuzzle.label;
    nextPuzzle.hidden = !model.nextPuzzle.visible;
    nextPuzzle.dataset.ready = String(model.nextPuzzle.ready);
    nextPuzzle.dataset.countdownFullDone = String(ids.countdownFullDone ?? false);
    nextPuzzle.dataset.loadButton = ids.nextPuzzleLoad;
  }
  if (nextPuzzleLoad) {
    nextPuzzleLoad.textContent = model.nextPuzzle.loadLabel;
    nextPuzzleLoad.hidden = !model.nextPuzzle.showLoadButton;
  }

  if (metrics) {
    metrics.replaceChildren();
    metrics.dataset.count = String(model.metrics.length);
    model.metrics.forEach((metric) => {
      const item = document.createElement('div');
      item.className = 'stage-metric';
      item.dataset.tone = metric.tone;
      const label = document.createElement('span');
      label.textContent = metric.label;
      const value = document.createElement('strong');
      value.textContent = metric.value;
      item.append(label, value);
      metrics.append(item);
    });
  }

  if (key9Cells) {
    key9Cells.replaceChildren();
    model.key9Strip.cells.forEach((slot) => {
      const cell = document.createElement('span');
      cell.className = 'stage-key9-cell';
      cell.dataset.state = slot.state;
      cell.dataset.digit = String(slot.digit);
      cell.textContent = slot.label;
      key9Cells.append(cell);
    });
  }

  if (fullBadge) {
    fullBadge.textContent = model.fullBadge.label;
    fullBadge.dataset.tone = model.fullBadge.tone;
  }
  syncNextPuzzleCountdownTimer();
}

function syncNextPuzzleCountdownTimer() {
  updateNextPuzzleCountdownElements();
  const hasVisibleCountdown = getVisibleCountdownElements().length > 0;
  if (hasVisibleCountdown && nextPuzzleTimerId == null) {
    nextPuzzleTimerId = window.setInterval(updateNextPuzzleCountdownElements, 1000);
  }
  if (!hasVisibleCountdown && nextPuzzleTimerId != null) {
    window.clearInterval(nextPuzzleTimerId);
    nextPuzzleTimerId = null;
  }
}

function updateNextPuzzleCountdownElements() {
  const visibleElements = getVisibleCountdownElements();
  document.querySelectorAll<HTMLElement>('[data-next-puzzle-countdown]').forEach((element) => {
    const model = getNextPuzzleCountdownModel(new Date(), {
      fullDone: element.dataset.countdownFullDone === 'true'
    });
    element.textContent = model.label;
    element.dataset.ready = String(model.ready);
    const loadButtonId = element.dataset.loadButton;
    const loadButton = loadButtonId ? document.getElementById(loadButtonId) as HTMLButtonElement | null : null;
    if (loadButton) {
      loadButton.textContent = model.loadLabel;
      loadButton.hidden = !model.showLoadButton;
    }
  });

  if (visibleElements.length === 0 && nextPuzzleTimerId != null) {
    window.clearInterval(nextPuzzleTimerId);
    nextPuzzleTimerId = null;
  }
}

function getVisibleCountdownElements(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-next-puzzle-countdown]')).filter((element) => (
    !element.hidden
    && element.closest('[hidden]') == null
    && window.getComputedStyle(element).display !== 'none'
  ));
}

function loadNewPuzzle() {
  activeBootToken += 1;
  window.location.reload();
}

function getInlineActionClass(primaryAction: 'share' | null, action: 'practice' | 'continue' | 'share'): string {
  return primaryAction === action ? 'key9-primary' : 'key9-secondary';
}

function updateGameControls(phase: GameControlPhase, notesMode = false, canHint = false) {
  applyGameShellState(getDayUiStateForPhase(phase));
  const controls = getGameControlState(phase);
  const actions = document.querySelector('[data-game-actions]') as HTMLElement | null;
  const secondaryActions = document.querySelector('[data-full-secondary-actions]') as HTMLElement | null;
  const viewResultButton = document.getElementById('ds-view-result') as HTMLButtonElement | null;
  const restartKey9Button = document.getElementById('ds-restart-key9') as HTMLButtonElement | null;
  if (actions) {
    actions.hidden = !controls.visible;
    actions.dataset.mode = controls.mode;
    actions.dataset.placement = controls.placement;
  }
  if (controls.layout) {
    actions?.style.setProperty('--ds-toolbar-top', `${(controls.layout.toolbarTopY / 630) * 100}%`);
  } else {
    actions?.style.removeProperty('--ds-toolbar-top');
  }
  if (secondaryActions) {
    secondaryActions.hidden = controls.secondaryActions.length === 0;
    secondaryActions.dataset.placement = controls.secondaryActionsPlacement;
  }
  const viewResult = controls.secondaryActions.find((action) => action.id === 'view_result');
  const restartKey9 = controls.secondaryActions.find((action) => action.id === 'restart_key9');
  if (viewResultButton) {
    viewResultButton.textContent = viewResult?.label ?? 'View Result';
    viewResultButton.hidden = !viewResult;
  }
  if (restartKey9Button) {
    restartKey9Button.textContent = restartKey9?.label ?? 'Restart Key9';
    restartKey9Button.hidden = !restartKey9;
  }
  if (controls.secondaryActions.length === 0) {
    hideFullAuxiliaryPanels();
  }
  setNotesButtonState(controls.visible && notesMode);
  setHintButtonState(controls.visible && canHint);
}

function applyGameShellState(state: DayUiState) {
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  if (!card) return;
  card.dataset.phase = state.phase;
  card.dataset.panel = state.panel;
  card.dataset.terminalVisible = String(state.showTerminalResult);
  card.dataset.terminalLayout = state.terminalLayout;
  if (!card.dataset.fullSubpanel) card.dataset.fullSubpanel = 'none';
  if (state.phase !== 'full_in_progress' || state.showTerminalResult) setFullSubpanelState('none', card);
  card.dataset.fullControlsVisible = String(state.showFullToolbar);
  card.dataset.key9InputVisible = String(state.showKey9Tiles);
  if (!card.dataset.previousResultVisible) card.dataset.previousResultVisible = 'false';
  if (!card.dataset.restartKey9ConfirmVisible) card.dataset.restartKey9ConfirmVisible = 'false';
  card.classList.toggle('ds-terminal-visible', state.showTerminalResult);
  card.classList.toggle('ds-terminal-hidden', !state.showTerminalResult);
  card.classList.toggle('ds-terminal-compact', state.terminalLayout === 'compact');
  card.classList.toggle('ds-full-controls-visible', state.showFullToolbar);
  card.classList.toggle('ds-full-controls-hidden', !state.showFullToolbar);
}

function getFullSubpanelState(): FullSubpanelState {
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  const value = card?.dataset.fullSubpanel;
  return value === 'view-result' || value === 'restart-key9' ? value : 'none';
}

function setFullSubpanelState(subpanel: FullSubpanelState, cardArg?: HTMLElement | null) {
  const card = cardArg ?? document.querySelector('[data-game-card]') as HTMLElement | null;
  if (!card) return;
  card.dataset.fullSubpanel = subpanel;
  card.classList.toggle('ds-full-subpanel-open', subpanel !== 'none');
  card.classList.toggle('ds-full-subpanel-view-result', subpanel === 'view-result');
  card.classList.toggle('ds-full-subpanel-restart-key9', subpanel === 'restart-key9');
}

function getCurrentGameShellState(): DayUiState {
  if (isTerminalResultVisible()) return getDayUiStateForPhase('key9_missed');
  const card = document.querySelector('[data-game-card]') as HTMLElement | null;
  const phase = card?.dataset.phase;
  return getDayUiStateForPhase(isKnownUiPhase(phase) ? phase : 'key9_in_progress');
}

function isTerminalResultVisible(): boolean {
  return isVisible(document.getElementById('ds-key9-actions'))
    || isVisible(document.getElementById('ds-result'))
    || isVisible(document.getElementById('ds-action-sheet'));
}

function isVisible(element: HTMLElement | null): boolean {
  if (!element || element.hidden) return false;
  return window.getComputedStyle(element).display !== 'none';
}

function isKnownUiPhase(value: string | undefined): Parameters<typeof getDayUiStateForPhase>[0] | false {
  return value === 'key9_in_progress'
    || value === 'key9_practice'
    || value === 'key9_unlocked'
    || value === 'key9_missed'
    || value === 'key9_practice_complete'
    || value === 'full_in_progress'
    || value === 'full_done'
    || value === 'active'
    ? value
    : false;
}

function setNotesButtonState(enabled: boolean) {
  const notesButton = document.getElementById('ds-notes') as HTMLButtonElement | null;
  notesButton?.setAttribute('aria-pressed', String(enabled));
}

function setHintButtonState(enabled: boolean) {
  const hintButton = document.getElementById('ds-hint') as HTMLButtonElement | null;
  if (!hintButton) return;
  if (enabled) {
    hintButton.removeAttribute('aria-disabled');
    hintButton.toggleAttribute('data-hint-ready', true);
    return;
  }

  hintButton.setAttribute('aria-disabled', 'true');
  hintButton.removeAttribute('data-hint-ready');
}

function setStatusLabel(label: string) {
  const statusLabel = document.getElementById('ds-status-label');
  if (statusLabel) statusLabel.textContent = label;
}

async function shareLatest() {
  if (!currentPuzzle) return;
  const save = loadSave();
  const shareDate = currentDailyDate();
  const text = latestShareVariant === 'full_done' && latestProgress?.phase === 'full_done'
    ? buildFullShareText(shareDate, latestProgress, save.currentStreak)
    : latestShareVariant === 'key9_practice_complete' && latestPracticeResult
      ? buildPracticeShareText(shareDate, latestPracticeResult, latestKey9Result, save.currentStreak)
      : latestKey9Result
        ? buildShareText(shareDate, latestKey9Result, save.currentStreak, { progress: latestProgress })
        : '';
  if (!text) return;
  if (navigator.share) {
    await navigator.share({ text }).catch(() => undefined);
    return;
  }
  await navigator.clipboard?.writeText(text).catch(() => undefined);
  alert('DaySudoku result copied.');
}

async function sharePreviousResult() {
  if (!currentPuzzle) return;
  const save = loadSave();
  const shareDate = currentDailyDate();
  const progress = getSavedDayProgress(save, shareDate, currentIdentity) ?? latestProgress;
  const key9Result = progress?.key9Result ?? getSavedKey9Result(save, shareDate, currentIdentity) ?? latestKey9Result;
  const text = previousResultShareVariant === 'full_done' && progress?.phase === 'full_done'
    ? buildFullShareText(shareDate, progress, save.currentStreak)
    : key9Result
      ? buildShareText(shareDate, key9Result, save.currentStreak, { progress })
      : '';
  if (!text) return;
  if (navigator.share) {
    await navigator.share({ text }).catch(() => undefined);
    return;
  }
  await navigator.clipboard?.writeText(text).catch(() => undefined);
  alert('DaySudoku result copied.');
}

boot();
